// Gantt Timeline Component - Main visualization area
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Task, Project, ViewMode, GanttConfig, TaskDependency,
  DependencyType 
} from '../types/gantt.types';
import { 
  calculateTaskPosition, 
  getDateRange, 
  formatDateHeader,
  isWeekend,
  getWorkingDays,
  calculateTaskDuration
} from '../utils/ganttCalculations';

interface GanttTimelineProps {
  tasks: Task[];
  projects: Project[];
  viewMode: ViewMode;
  zoom: number;
  config: GanttConfig;
  onTaskClick: (task: Task) => void;
  onTaskDrag: (taskId: string, newStartDate: Date) => void;
  onTaskResize: (taskId: string, newEndDate: Date) => void;
  onProgressUpdate: (taskId: string, progress: number) => void;
  onDependencyCreate: (from: string, to: string, type: DependencyType) => void;
  onDependencyDelete: (dependencyId: string) => void;
}

export const GanttTimeline: React.FC<GanttTimelineProps> = ({
  tasks,
  projects,
  viewMode,
  zoom,
  config,
  onTaskClick,
  onTaskDrag,
  onTaskResize,
  onProgressUpdate,
  onDependencyCreate,
  onDependencyDelete
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [dragging, setDragging] = useState<{
    taskId: string;
    type: 'move' | 'resize-left' | 'resize-right' | 'progress';
    startX: number;
    originalValue: any;
  } | null>(null);
  
  const [hoveredTask, setHoveredTask] = useState<string | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [connectingDependency, setConnectingDependency] = useState<{
    from: string;
    x: number;
    y: number;
  } | null>(null);

  // Calculate timeline dimensions
  const { startDate, endDate, totalDays } = getDateRange(tasks, viewMode);
  const dayWidth = (40 * zoom) / 100;
  const rowHeight = 40;
  const headerHeight = 60;
  const totalWidth = totalDays * dayWidth;
  const totalHeight = tasks.length * rowHeight + headerHeight;

  // Group tasks by project
  const tasksByProject = tasks.reduce((acc, task) => {
    if (!acc[task.project_id]) acc[task.project_id] = [];
    acc[task.project_id].push(task);
    return acc;
  }, {} as Record<string, Task[]>);

  // Render task bar
  const renderTask = (task: Task, index: number) => {
    const { x, width } = calculateTaskPosition(task, startDate, dayWidth);
    const y = index * rowHeight + headerHeight;
    const isHovered = hoveredTask === task.id;
    const isSelected = selectedTasks.includes(task.id);
    const isCritical = task.is_critical_path;
    const isDelayed = task.status === 'delayed';
    const isMilestone = task.type === 'milestone';
    
    // Task color based on status and priority
    let taskColor = config.colors.task;
    if (task.status === 'completed') taskColor = config.colors.completed;
    if (task.status === 'delayed') taskColor = config.colors.delayed;
    if (isCritical) taskColor = config.colors.critical;
    if (isMilestone) taskColor = config.colors.milestone;
    
    // Override with task custom color if set
    if (task.color) taskColor = task.color;

    return (
      <g key={task.id} className="task-group">
        {/* Task background */}
        <rect
          x={x}
          y={y + 5}
          width={width}
          height={30}
          rx={isMilestone ? 0 : 4}
          fill={taskColor}
          fillOpacity={0.8}
          stroke={isSelected ? '#fff' : taskColor}
          strokeWidth={isSelected ? 2 : 1}
          className="cursor-pointer transition-all hover:fill-opacity-100"
          onClick={() => onTaskClick(task)}
          onMouseEnter={() => setHoveredTask(task.id)}
          onMouseLeave={() => setHoveredTask(null)}
        />
        
        {/* Progress bar */}
        {!isMilestone && config.showProgress && (
          <rect
            x={x}
            y={y + 5}
            width={width * (task.progress / 100)}
            height={30}
            rx={4}
            fill={taskColor}
            fillOpacity={1}
            className="pointer-events-none"
          />
        )}
        
        {/* Task name */}
        <text
          x={x + 8}
          y={y + 23}
          fill="white"
          fontSize="12"
          fontWeight="500"
          className="pointer-events-none select-none"
        >
          {task.name.length > 20 ? `${task.name.substring(0, 20)}...` : task.name}
        </text>
        
        {/* Progress percentage */}
        {!isMilestone && config.showProgress && (
          <text
            x={x + width - 8}
            y={y + 23}
            fill="white"
            fontSize="11"
            textAnchor="end"
            className="pointer-events-none select-none"
          >
            {task.progress}%
          </text>
        )}
        
        {/* Resize handles */}
        {isHovered && !isMilestone && (
          <>
            <rect
              x={x - 4}
              y={y + 10}
              width={8}
              height={20}
              fill="white"
              fillOpacity={0.5}
              className="cursor-ew-resize"
              onMouseDown={(e) => handleResizeStart(e, task.id, 'left')}
            />
            <rect
              x={x + width - 4}
              y={y + 10}
              width={8}
              height={20}
              fill="white"
              fillOpacity={0.5}
              className="cursor-ew-resize"
              onMouseDown={(e) => handleResizeStart(e, task.id, 'right')}
            />
          </>
        )}
        
        {/* Dependency connection points */}
        {config.showDependencies && (
          <>
            <circle
              cx={x}
              cy={y + 20}
              r={4}
              fill="white"
              stroke={taskColor}
              strokeWidth={2}
              className="cursor-crosshair opacity-0 hover:opacity-100 transition-opacity"
              onMouseDown={(e) => handleDependencyStart(e, task.id, 'start')}
            />
            <circle
              cx={x + width}
              cy={y + 20}
              r={4}
              fill="white"
              stroke={taskColor}
              strokeWidth={2}
              className="cursor-crosshair opacity-0 hover:opacity-100 transition-opacity"
              onMouseDown={(e) => handleDependencyStart(e, task.id, 'end')}
            />
          </>
        )}
      </g>
    );
  };

  // Render dependencies
  const renderDependencies = () => {
    const dependencies: JSX.Element[] = [];
    
    tasks.forEach(task => {
      task.dependencies?.forEach(dep => {
        const fromTask = tasks.find(t => t.id === dep.task_id);
        const toTask = tasks.find(t => t.id === dep.dependent_task_id);
        
        if (fromTask && toTask) {
          const fromPos = calculateTaskPosition(fromTask, startDate, dayWidth);
          const toPos = calculateTaskPosition(toTask, startDate, dayWidth);
          const fromIndex = tasks.indexOf(fromTask);
          const toIndex = tasks.indexOf(toTask);
          
          let x1 = fromPos.x + fromPos.width;
          let y1 = fromIndex * rowHeight + headerHeight + 20;
          let x2 = toPos.x;
          let y2 = toIndex * rowHeight + headerHeight + 20;
          
          // Adjust based on dependency type
          if (dep.type === 'SS') {
            x1 = fromPos.x;
            x2 = toPos.x;
          } else if (dep.type === 'FF') {
            x1 = fromPos.x + fromPos.width;
            x2 = toPos.x + toPos.width;
          } else if (dep.type === 'SF') {
            x1 = fromPos.x;
            x2 = toPos.x + toPos.width;
          }
          
          // Draw arrow path
          const midX = (x1 + x2) / 2;
          const path = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
          
          dependencies.push(
            <g key={dep.id}>
              <path
                d={path}
                stroke="#666"
                strokeWidth={2}
                fill="none"
                strokeDasharray={dep.type === 'FS' ? undefined : "5,5"}
                markerEnd="url(#arrowhead)"
                className="hover:stroke-orange-500 cursor-pointer transition-colors"
                onClick={() => onDependencyDelete(dep.id)}
              />
            </g>
          );
        }
      });
    });
    
    return dependencies;
  };

  // Render timeline header
  const renderHeader = () => {
    const headers: JSX.Element[] = [];
    const currentDate = new Date(startDate);
    
    for (let i = 0; i < totalDays; i++) {
      const x = i * dayWidth;
      const dateStr = formatDateHeader(currentDate, viewMode);
      const isWeekendDay = isWeekend(currentDate);
      const isToday = currentDate.toDateString() === new Date().toDateString();
      
      headers.push(
        <g key={i}>
          {/* Day column background */}
          <rect
            x={x}
            y={0}
            width={dayWidth}
            height={totalHeight}
            fill={isWeekendDay ? 'rgba(255,255,255,0.02)' : 'transparent'}
            stroke="rgba(255,255,255,0.1)"
            strokeWidth={0.5}
          />
          
          {/* Today marker */}
          {isToday && (
            <rect
              x={x}
              y={0}
              width={dayWidth}
              height={totalHeight}
              fill="rgba(251,146,60,0.1)"
              className="pointer-events-none"
            />
          )}
          
          {/* Header text */}
          <text
            x={x + dayWidth / 2}
            y={30}
            fill="white"
            fontSize="12"
            textAnchor="middle"
            className="select-none"
          >
            {dateStr}
          </text>
        </g>
      );
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return headers;
  };

  // Mouse event handlers
  const handleResizeStart = (e: React.MouseEvent, taskId: string, side: 'left' | 'right') => {
    e.stopPropagation();
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    setDragging({
      taskId,
      type: side === 'left' ? 'resize-left' : 'resize-right',
      startX: e.clientX,
      originalValue: side === 'left' ? task.start_date : task.end_date
    });
  };

  const handleDependencyStart = (e: React.MouseEvent, taskId: string, point: 'start' | 'end') => {
    e.stopPropagation();
    setConnectingDependency({
      from: taskId,
      x: e.clientX,
      y: e.clientY
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (dragging) {
      const deltaX = e.clientX - dragging.startX;
      const daysDelta = Math.round(deltaX / dayWidth);
      
      if (dragging.type === 'move') {
        // Handle task move
        const newStartDate = new Date(dragging.originalValue);
        newStartDate.setDate(newStartDate.getDate() + daysDelta);
        // Preview the move (update local state)
      } else if (dragging.type === 'resize-right') {
        // Handle resize
        const newEndDate = new Date(dragging.originalValue);
        newEndDate.setDate(newEndDate.getDate() + daysDelta);
        // Preview the resize
      }
    }
    
    if (connectingDependency) {
      // Update connection line preview
    }
  }, [dragging, connectingDependency, dayWidth]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (dragging) {
      const deltaX = e.clientX - dragging.startX;
      const daysDelta = Math.round(deltaX / dayWidth);
      
      if (dragging.type === 'move') {
        const newStartDate = new Date(dragging.originalValue);
        newStartDate.setDate(newStartDate.getDate() + daysDelta);
        onTaskDrag(dragging.taskId, newStartDate);
      } else if (dragging.type === 'resize-right') {
        const newEndDate = new Date(dragging.originalValue);
        newEndDate.setDate(newEndDate.getDate() + daysDelta);
        onTaskResize(dragging.taskId, newEndDate);
      }
      
      setDragging(null);
    }
    
    if (connectingDependency) {
      // Check if mouse is over a task
      // If yes, create dependency
      setConnectingDependency(null);
    }
  }, [dragging, connectingDependency, dayWidth, onTaskDrag, onTaskResize]);

  // Add event listeners
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-auto bg-zinc-900"
      style={{ minHeight: totalHeight }}
    >
      <svg
        ref={svgRef}
        width={totalWidth}
        height={totalHeight}
        className="gantt-timeline"
      >
        {/* Define arrow marker */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3, 0 6"
              fill="#666"
            />
          </marker>
        </defs>
        
        {/* Grid and headers */}
        <g className="timeline-grid">
          {renderHeader()}
        </g>
        
        {/* Row backgrounds */}
        <g className="row-backgrounds">
          {tasks.map((_, index) => (
            <rect
              key={index}
              x={0}
              y={index * rowHeight + headerHeight}
              width={totalWidth}
              height={rowHeight}
              fill={index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}
              stroke="rgba(255,255,255,0.05)"
              strokeWidth={0.5}
            />
          ))}
        </g>
        
        {/* Dependencies */}
        {config.showDependencies && (
          <g className="dependencies">
            {renderDependencies()}
          </g>
        )}
        
        {/* Tasks */}
        <g className="tasks">
          {tasks.map((task, index) => renderTask(task, index))}
        </g>
        
        {/* Connection line preview */}
        {connectingDependency && (
          <line
            x1={connectingDependency.x}
            y1={connectingDependency.y}
            x2={connectingDependency.x}
            y2={connectingDependency.y}
            stroke="orange"
            strokeWidth={2}
            strokeDasharray="5,5"
            className="pointer-events-none"
          />
        )}
      </svg>
    </div>
  );
};